import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, FileCheck, FolderSearch, Clock, AlertTriangle, LifeBuoy, CreditCard } from "lucide-react";
import { API } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { Stat, PageHeader, Loading, StatusBadge, Money, Empty } from "../../components/common";

export default function TaxProDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { API.get("/admin/taxpro/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading label="Loading your dashboard" />;

  return (
    <div data-testid="taxpro-dashboard">
      <PageHeader title="Tax Professional Dashboard" sub="Your assigned clients, returns due, reviews, and rescue workload." />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
        <Stat label="Clients" value={data.clients_total} tone="navy" icon={Users} testid="tp-clients" />
        <Stat label="Current" value={data.clients_current} tone="green" icon={FileCheck} testid="tp-current" />
        <Stat label="Ready to File" value={data.ready_to_file} tone="blue" icon={FileCheck} testid="tp-ready" />
        <Stat label="Missing Docs" value={data.missing_documents} tone="yellow" icon={FolderSearch} testid="tp-missing-docs" />
        <Stat label="Past Due" value={data.past_due} tone="red" icon={AlertTriangle} testid="tp-past-due" />
        <Stat label="Payment Issues" value={data.payment_issues} tone="red" icon={CreditCard} testid="tp-payment-issues" />
        <Stat label="Rescue Cases" value={data.rescue_cases} tone="yellow" icon={LifeBuoy} testid="tp-rescue" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="card-gov p-5">
          <h3 className="mb-3 font-bold text-navy">Returns Needing Review / Approval</h3>
          {data.ready_periods.length === 0 ? <Empty title="Nothing awaiting review" /> : (
            <ul className="space-y-2">
              {data.ready_periods.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2" data-testid={`tp-period-${p.id}`}>
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{p.label}</div>
                    <div className="text-xs text-slate-400">Due {fmtDate(p.due_date)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Money cents={p.liability_cents} className="text-sm font-bold text-navy" />
                    <StatusBadge status={p.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link to="/admin/work-queue" className="btn-outline-gov mt-4 w-full" data-testid="tp-open-queue">Open Work Queue</Link>
        </div>

        <div className="card-gov p-5">
          <h3 className="mb-3 font-bold text-navy">Upcoming Deadlines</h3>
          {data.due_soon.length === 0 ? <Empty title="No upcoming filings" /> : (
            <ul className="space-y-2">
              {data.due_soon.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-semibold text-slate-800">{p.label}</span>
                  </div>
                  <span className="text-xs text-slate-500">{fmtDate(p.due_date)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-6 card-gov p-5">
        <h3 className="mb-3 font-bold text-navy">Assigned Clients</h3>
        {data.clients.length === 0 ? <Empty title="No clients assigned" /> : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {data.clients.map((c) => (
              <Link key={c.id} to={`/admin/customers/${c.id}`} className="rounded-md border border-slate-200 px-3 py-2.5 hover:border-navy" data-testid={`tp-client-${c.case_number}`}>
                <div className="text-sm font-semibold text-navy">{c.legal_name}</div>
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span className="font-mono">{c.case_number}</span>
                  <StatusBadge status={c.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
